import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    doc,
    query,
    where,
    Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

const REMINDERS_COLLECTION = 'reminders';
// Changed from 'users' to 'employees' as that's where employee data with DOB/JoiningDate usually lives
const USERS_COLLECTION = 'employees';

/**
 * Get all reminders - generates dynamic events from employee birthdays and anniversaries
 * @param {number} daysAhead - Number of days to look ahead (default: 30)
 * @param {string} type - Filter by type: 'birthday', 'work_anniversary', or null for all
 */
export const getReminders = async (daysAhead = 30, type = null) => {
    try {
        console.log('[ReminderService] Fetching reminders, daysAhead:', daysAhead, 'type:', type);
        
        // First try to get stored reminders
        const remindersRef = collection(db, REMINDERS_COLLECTION);
        const q = query(remindersRef);

        const snapshot = await getDocs(q);
        let reminders = [];

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            reminders.push({
                id: docSnap.id,
                ...data,
                date: data.date?.toDate?.()?.toISOString() || data.date,
                createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
            });
        });

        // Always generate dynamic reminders from employee data
        const dynamicEvents = await generateDynamicEvents(daysAhead);
        console.log('[ReminderService] Generated', dynamicEvents.length, 'dynamic events');
        
        // Merge stored and dynamic reminders
        reminders = [...reminders, ...dynamicEvents];

        // Filter by type if specified
        if (type) {
            reminders = reminders.filter(r => r.type === type);
        }

        // Sort by daysUntil (soonest first)
        reminders.sort((a, b) => (a.daysUntil || 999) - (b.daysUntil || 999));

        console.log('[ReminderService] Returning', reminders.length, 'reminders');
        return {
            success: true,
            data: reminders
        };
    } catch (error) {
        console.error('Error fetching reminders:', error);
        return { success: true, data: [] };
    }
};

/**
 * Mark reminder as read
 */
export const markAsRead = async (reminderId) => {
    try {
        // If it's a virtual/dynamic reminder (no real ID), we can't update DB
        if (reminderId.startsWith('dynamic_')) {
            return { success: true, message: 'Marked read locally' };
        }

        const reminderRef = doc(db, REMINDERS_COLLECTION, reminderId);

        await updateDoc(reminderRef, {
            isRead: true,
            updatedAt: Timestamp.now()
        });

        return {
            success: true,
            message: 'Reminder marked as read'
        };
    } catch (error) {
        console.error('Error marking reminder as read:', error);
        throw new Error(error.message || 'Failed to mark reminder as read');
    }
};

/**
 * Check for upcoming birthdays and anniversaries
 * This would typically be run by a scheduled job, but can be called manually
 */
export const checkUpcomingEvents = async () => {
    // Kept for manual triggering, but getReminders now does this dynamically
    return await generateDynamicEvents();
};

// Helper to generate events on the fly without saving to DB (read-only view)
const generateDynamicEvents = async (lookAheadDays = 30) => {
    try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        console.log('[ReminderService] Generating dynamic events, lookAheadDays:', lookAheadDays);

        // Get all employees (not just active, we'll filter)
        const usersRef = collection(db, USERS_COLLECTION);
        const usersSnapshot = await getDocs(query(usersRef));
        
        console.log('[ReminderService] Found', usersSnapshot.size, 'employees');

        const events = [];

        usersSnapshot.forEach((userDoc) => {
            const user = userDoc.data();
            
            // Skip deleted or inactive employees
            if (user.isDeleted === true) return;
            const isActive = user.status === 'Active' || user.isActive === true || user.status === 'Probation' || user.status === undefined;
            if (!isActive) return;

            // Handle both nested and flat structures
            const personal = user.personal || {};
            const job = user.job || {};
            
            const userName = personal.fullName ||
                `${personal.firstName || user.firstName || ''} ${personal.lastName || user.lastName || ''}`.trim() ||
                user.employeeName ||
                'Unknown';

            // Check birthday
            const dob = personal.dateOfBirth || user.dob || user.dateOfBirth;
            if (dob) {
                const birthdayEvent = checkDateMatch(dob, 'birthday', userName, userDoc.id, lookAheadDays);
                if (birthdayEvent) events.push(birthdayEvent);
            }

            // Check work anniversary
            const joiningDate = job.joiningDate || user.joiningDate;
            if (joiningDate) {
                const anniversaryEvent = checkDateMatch(joiningDate, 'work_anniversary', userName, userDoc.id, lookAheadDays);
                if (anniversaryEvent) events.push(anniversaryEvent);
            }
        });

        return events;
    } catch (error) {
        console.error("Error generating dynamic events:", error);
        return [];
    }
};

const checkDateMatch = (dateStr, type, userName, userId, lookAheadDays) => {
    try {
        const eventDate = dateStr?.toDate ? dateStr.toDate() : new Date(dateStr);
        if (isNaN(eventDate.getTime())) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const currentYear = today.getFullYear();
        const eventMonth = eventDate.getMonth();
        const eventDay = eventDate.getDate();

        // Create date object for this year's occurrence
        let eventThisYear = new Date(currentYear, eventMonth, eventDay);

        // If event has passed this year, look at next year
        if (eventThisYear < today) {
            eventThisYear = new Date(currentYear + 1, eventMonth, eventDay);
        }

        // Calculate difference in days
        const diffTime = eventThisYear.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays >= 0 && diffDays <= lookAheadDays) {
            let years = 0;
            if (type === 'work_anniversary') {
                years = eventThisYear.getFullYear() - eventDate.getFullYear();
                // Skip if less than 1 year
                if (years < 1) return null;
            } else {
                years = eventThisYear.getFullYear() - eventDate.getFullYear(); // Age
            }

            let message = '';
            const dateString = eventThisYear.toLocaleDateString();
            if (type === 'work_anniversary') {
                message = diffDays === 0
                    ? `Happy Work Anniversary ${userName} (${years} years)!`
                    : `Upcoming Anniversary: ${userName} (${years} years) on ${dateString}`;
            } else {
                message = diffDays === 0
                    ? `Happy Birthday ${userName}!`
                    : `Upcoming Birthday: ${userName} on ${dateString}`;
            }

            return {
                id: `dynamic_${type}_${userId}_${eventThisYear.getFullYear()}`,
                type: type, // 'birthday' or 'work_anniversary'
                employeeName: userName,
                daysUntil: diffDays,
                years: years,
                message: message,
                date: eventThisYear.toISOString(),
                createdAt: new Date().toISOString(),
                isRead: false
            };
        }
    } catch (e) {
        console.warn("Date parsing error for", userName, e);
    }
    return null;
};

const reminderService = {
    getReminders,
    markAsRead,
    checkUpcomingEvents
};

export default reminderService;
